import { useState } from "react";
import { cn } from "@/shared/lib/utils";
import { InfoHint } from "@/shared/components/ui/info-hint";

/**
 * Presentational building blocks shared by the lead detail drawer (ADR-0020) and the
 * full lead-profile page — a labelled section, a label:value field, a collapsible raw
 * JSON block, and the enriched-company block. Kept here so both surfaces render the
 * collected data identically (no drift).
 */

export function Section({
  title,
  hint,
  action,
  children,
}: {
  title: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="ge-eyebrow flex items-center gap-1">
          {title}
          {hint && <InfoHint text={hint} label={`About ${title}`} />}
        </p>
        {action}
      </div>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

export function Field({
  label,
  value,
  mono,
  link,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
  link?: boolean;
}) {
  const v = value ?? null;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="shrink-0 font-mono text-[11px] text-ge-muted">{label}</span>
      {v == null ? (
        <span className="font-mono text-[12px] text-ge-faint">—</span>
      ) : link ? (
        <a
          href={/^https?:\/\//i.test(v) ? v : `https://${v}`}
          target="_blank"
          rel="noreferrer noopener"
          className="truncate font-mono text-[12px] text-ge-accent hover:underline"
        >
          {v}
        </a>
      ) : (
        <span className={cn("truncate text-right text-[12.5px] text-ge-value", mono && "font-mono text-[11.5px]")}>{v}</span>
      )}
    </div>
  );
}

/** Renders the enriched company firmographics stored under aiData.enrichment.company (ADR-0024). */
export function CompanyBlock({ aiData }: { aiData: unknown }) {
  const company = (aiData as { enrichment?: { company?: Record<string, unknown> } } | null)?.enrichment?.company;
  if (!company) return null;
  const fields: { label: string; key: string; link?: boolean }[] = [
    { label: "Name", key: "name" },
    { label: "Industry", key: "industry" },
    { label: "Headcount", key: "headcount" },
    { label: "Country", key: "country" },
    { label: "Type", key: "type" },
    { label: "LinkedIn", key: "linkedin", link: true },
  ];
  const present = fields.filter((f) => company[f.key] != null && company[f.key] !== "");
  if (present.length === 0) return null;
  return (
    <Section title="Company (enriched)">
      {present.map((f) => (
        <Field key={f.key} label={f.label} value={String(company[f.key])} link={f.link} mono={f.link} />
      ))}
    </Section>
  );
}

export function RawBlock({ title, value }: { title: string; value: unknown }) {
  const [open, setOpen] = useState(false);
  const empty = value == null || (typeof value === "object" && Object.keys(value as object).length === 0);
  if (empty) return null;
  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`raw-toggle-${title.replace(/\s+/g, "-").toLowerCase()}`}
        className="ge-eyebrow flex items-center gap-1 hover:text-ge-value"
      >
        {open ? "▾" : "▸"} {title}
      </button>
      {open && (
        <pre className="mt-2 max-h-[280px] overflow-auto rounded-ge-param border border-ge-border-card bg-ge-card p-3 font-mono text-[11px] text-ge-secondary">
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </section>
  );
}
